import React, { useState } from 'react';

interface DemoReel {
  id: string;
  title: string;
  niche: string;
  hook: string;
  thumbnail: string;
  audioUrl: string;
  stats: {
    generationTime: string;
    wordCount: number;
    shots: number;
    voice: string;
  };
  before: {
    description: string;
    challenge: string;
  };
  after: {
    description: string;
    improvement: string;
  };
}

export const DemoGallery: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState<string>('productivity');
  const [isPlaying, setIsPlaying] = useState<string | null>(null);

  const demoReels: DemoReel[] = [
    {
      id: 'productivity',
      title: 'Productivity Hacks for Remote Workers',
      niche: 'Business/Productivity',
      hook: 'Stop doing productivity wrong in 2024...',
      thumbnail: 'https://assets.jonkai.com/autoreelr/demos/productivity-thumb.jpg',
      audioUrl: 'https://assets.jonkai.com/autoreelr/demos/productivity-audio.mp3',
      stats: {
        generationTime: '3m 42s',
        wordCount: 127,
        shots: 5,
        voice: 'Asteria (Female)'
      },
      before: {
        description: 'Manual creation would require: research, scripting, filming/sourcing footage, editing, voiceover recording, thumbnail design',
        challenge: 'Estimated 3-5 hours of work + professional tools + design skills'
      },
      after: {
        description: 'AI-generated complete reel with viral hook, professional script, voiceover, and thumbnail',
        improvement: '95% time saved, professional quality, zero technical skills required'
      }
    },
    {
      id: 'finance',
      title: 'Passive Income Strategies for 2024', 
      niche: 'Finance/Investment',
      hook: 'These 3 passive income streams changed everything...',
      thumbnail: 'https://assets.jonkai.com/autoreelr/demos/finance-thumb.jpg',
      audioUrl: 'https://assets.jonkai.com/autoreelr/demos/finance-audio.mp3',
      stats: {
        generationTime: '4m 18s',
        wordCount: 143,
        shots: 6,
        voice: 'Luna (Female)'
      },
      before: {
        description: 'Traditional approach: market research, script writing, stock footage licensing, audio recording, editing',
        challenge: 'Requires financial expertise, video editing skills, expensive software licenses'
      },
      after: {
        description: 'Expert-level content with data-driven insights, professional presentation, engaging visuals',
        improvement: '90% cost reduction, faster time-to-market, consistent quality'
      }
    },
    {
      id: 'lifestyle',
      title: 'Morning Routines That Actually Work',
      niche: 'Health/Lifestyle', 
      hook: 'I tested 30 morning routines so you don\'t have to...',
      thumbnail: 'https://assets.jonkai.com/autoreelr/demos/lifestyle-thumb.jpg',
      audioUrl: 'https://assets.jonkai.com/autoreelr/demos/lifestyle-audio.mp3',
      stats: {
        generationTime: '3m 28s',
        wordCount: 118,
        shots: 4,
        voice: 'Stella (Female)'
      },
      before: {
        description: 'Content creation challenges: personal experience documentation, lifestyle footage, wellness expertise',
        challenge: 'Time-intensive filming, seasonal content limitations, personal branding requirements'
      },
      after: {
        description: 'Evidence-based wellness content with actionable tips, aesthetic visuals, motivational delivery',
        improvement: 'Scalable content creation, no personal filming required, year-round relevance'
      }
    },
    {
      id: 'tech',
      title: 'Coding Tips Every Developer Needs',
      niche: 'Technology/Programming',
      hook: 'These coding habits will 10x your productivity...',
      thumbnail: 'https://assets.jonkai.com/autoreelr/demos/tech-thumb.jpg',
      audioUrl: 'https://assets.jonkai.com/autoreelr/demos/tech-audio.mp3',
      stats: {
        generationTime: '4m 05s',
        wordCount: 156,
        shots: 7,
        voice: 'Perseus (Male)'
      },
      before: {
        description: 'Technical content challenges: code demonstrations, screen recording, complex explanations',
        challenge: 'Technical expertise required, complex editing, specialized equipment needs'
      },
      after: {
        description: 'Clear technical explanations, engaging visual metaphors, accessible to all skill levels',
        improvement: 'Broader audience reach, simplified production, consistent educational value'
      }
    }
  ];

  const activeDemoData = demoReels.find(demo => demo.id === activeDemo) || demoReels[0];

  const playAudio = (demoId: string) => {
    setIsPlaying(demoId);
    // In a real implementation, you'd handle audio playback here
    setTimeout(() => setIsPlaying(null), 3000); // Simulate audio duration
  };

  return (
    <div className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Real Results from Real Creators
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            See how AutoReelr transforms simple ideas into professional, engaging reels 
            across different niches and content styles.
          </p>
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md font-semibold">
              Demo Gallery
            </button>
            <button className="px-4 py-2 text-gray-600 hover:text-gray-900">
              Case Studies
            </button>
            <button className="px-4 py-2 text-gray-600 hover:text-gray-900">
              Creator Stories
            </button>
          </div>
        </div>

        {/* Demo Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {demoReels.map((demo) => (
            <button
              key={demo.id}
              onClick={() => setActiveDemo(demo.id)}
              className={`p-6 rounded-lg text-left transition-all ${
                activeDemo === demo.id
                  ? 'bg-blue-50 border-2 border-blue-500 shadow-lg'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <div className="text-sm text-blue-600 font-semibold mb-2">{demo.niche}</div>
              <h3 className="font-bold text-gray-900 mb-2">{demo.title}</h3>
              <p className="text-gray-600 text-sm italic">"{demo.hook}"</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-gray-500">{demo.stats.generationTime}</span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  {demo.stats.shots} shots
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Active Demo Display */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Demo Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <div className="text-blue-200 text-sm font-semibold mb-2">
                  {activeDemoData.niche}
                </div>
                <h3 className="text-3xl font-bold mb-2">{activeDemoData.title}</h3>
                <p className="text-xl italic text-blue-100">
                  "{activeDemoData.hook}"
                </p>
              </div>
              <div className="mt-4 md:mt-0 bg-white/20 backdrop-blur-sm rounded-lg p-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{activeDemoData.stats.generationTime}</div>
                  <div className="text-sm text-blue-200">Generation Time</div>
                </div>
              </div>
            </div>
          </div>

          {/* Demo Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: Media */}
            <div className="p-8">
              <h4 className="text-xl font-bold text-gray-900 mb-6">Generated Content</h4>
              
              {/* Thumbnail */}
              <div className="mb-6">
                <h5 className="font-semibold text-gray-700 mb-3">AI-Generated Thumbnail:</h5>
                <div className="relative group">
                  <img 
                    src={activeDemoData.thumbnail}
                    alt="Generated thumbnail"
                    className="w-full rounded-lg shadow-lg"
                    onError={(e) => {
                      // Fallback for missing images
                      e.currentTarget.src = `data:image/svg+xml;base64,${btoa(`
                        <svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
                          <rect width="100%" height="100%" fill="#f3f4f6"/>
                          <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#6b7280" font-family="Arial" font-size="16">
                            ${activeDemoData.title}
                          </text>
                        </svg>
                      `)}`;
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <span className="text-white font-semibold">Generated in 15s</span>
                  </div>
                </div>
              </div>

              {/* Audio */}
              <div className="mb-6">
                <h5 className="font-semibold text-gray-700 mb-3">Professional Voiceover:</h5>
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-600">Voice: {activeDemoData.stats.voice}</span>
                    <button
                      onClick={() => playAudio(activeDemoData.id)}
                      disabled={isPlaying === activeDemoData.id}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isPlaying === activeDemoData.id ? '🔊 Playing...' : '▶️ Play Preview'}
                    </button>
                  </div>
                  <audio 
                    controls 
                    className="w-full"
                    onError={(e) => {
                      // Hide broken audio element
                      e.currentTarget.style.display = 'none';
                    }}
                  >
                    <source src={activeDemoData.audioUrl} type="audio/mpeg" />
                    Your browser does not support audio playback.
                  </audio>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{activeDemoData.stats.wordCount}</div>
                  <div className="text-sm text-blue-800">Words</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{activeDemoData.stats.shots}</div>
                  <div className="text-sm text-green-800">Shots</div>
                </div>
              </div>
            </div>

            {/* Right: Before/After */}
            <div className="p-8 bg-gray-50">
              <h4 className="text-xl font-bold text-gray-900 mb-6">Before vs After AutoReelr</h4>
              
              {/* Before */}
              <div className="mb-8">
                <div className="flex items-center mb-3">
                  <span className="text-red-500 text-xl mr-2">❌</span>
                  <h5 className="font-semibold text-gray-900">Traditional Method</h5>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-3">
                  <p className="text-gray-700 text-sm mb-2">{activeDemoData.before.description}</p>
                  <p className="text-red-700 text-sm font-semibold">{activeDemoData.before.challenge}</p>
                </div>
              </div>

              {/* After */}
              <div>
                <div className="flex items-center mb-3">
                  <span className="text-green-500 text-xl mr-2">✅</span>
                  <h5 className="font-semibold text-gray-900">With AutoReelr</h5>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3">
                  <p className="text-gray-700 text-sm mb-2">{activeDemoData.after.description}</p>
                  <p className="text-green-700 text-sm font-semibold">{activeDemoData.after.improvement}</p>
                </div>
              </div>

              {/* ROI Calculation */}
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h6 className="font-semibold text-yellow-800 mb-2">💰 ROI Calculation</h6>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600">Manual Cost:</div>
                    <div className="font-semibold">$300-500</div>
                    <div className="text-xs text-gray-500">(3-5 hours @ $100/hr)</div>
                  </div>
                  <div>
                    <div className="text-gray-600">AutoReelr Cost:</div>
                    <div className="font-semibold text-green-600">$5-10</div>
                    <div className="text-xs text-gray-500">(5 minutes + AI credits)</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-yellow-300">
                  <div className="text-center font-bold text-yellow-800">
                    💡 98% Cost Reduction + 95% Time Savings
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gray-900 p-8 text-center">
            <h4 className="text-2xl font-bold text-white mb-4">
              Create Your Own Professional Reel
            </h4>
            <p className="text-gray-300 mb-6">
              Join thousands of creators who've transformed their content workflow
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-3 rounded-lg font-bold hover:from-yellow-300 hover:to-orange-400 transition-all">
                🚀 Start Creating Now
              </button>
              <button className="bg-white/20 text-white px-8 py-3 rounded-lg font-bold hover:bg-white/30 transition-all border border-white/30">
                📞 Book a Demo Call
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoGallery;